import type { Checkin, CookLogEntry, GameMeta } from '@awwyeah/shared';
import { dayKey, dayLabel } from './feed';

/**
 * Games — and now cooks — in the family timeline (ROADMAP "Games in the family timeline";
 * owner ask 2026-08-18 for "Cooked this").
 *
 * The check-in feed reads the family `GAMES_DOC` registry and the `COOKS_DOC` cook log
 * (both read-only — see `./games.ts` and `./cooks.ts`) alongside its own `CHECKINS_DOC` map
 * and interleaves them chronologically as quiet fact cards: "Dan & Faye played Battleship",
 * the outcome, tap to replay; "Dan cooked Smoked salmon", tap to open the recipe. This is a
 * **read-side merge only** — no data duplication, no new doc *here*, no write path. Each
 * fact already exists in full in the doc that owns it; the feed just also shows it where it
 * belongs in time.
 *
 * Deliberate exclusions, which now cover three kinds rather than two:
 *
 *  - **No `seenBy` on game entries.** `seenBy` is a check-in *authoring* concept — someone
 *    posted a thing, and other members' attention to that thing is worth recording
 *    (`components/SeenByRow.tsx`). A finished game is not authored, it's *derived*: it
 *    already exists as a fact in the games registry the moment `status` flips to `'done'`,
 *    with no author to have posted it and no "have you seen this yet" question that means
 *    anything for a fact everyone already has equal access to. `FeedItem`'s `game` variant
 *    has no `seenBy` field for the same reason `Checkin.photoRef` doesn't have a page
 *    count — it isn't a property of the thing.
 *  - **No `seenBy` on cook entries either — but for a different reason, and the difference
 *    matters.** A cook *is* deliberately authored: somebody tapped "Cooked this" with the
 *    intent of telling the house. So the games argument ("derived, nobody posted it") does
 *    not transfer, and it would have been dishonest to reuse it. What does transfer is the
 *    second half: `seenBy` exists because a check-in carries a *payload* — a photo you
 *    could miss — and its author is owed some signal that it landed. A cook carries no
 *    payload. The recipe was already in the box, unchanged; the entry adds one sentence
 *    that is fully readable from the feed row itself, so there is no "have you looked at it
 *    yet" question with an answer. Worse, a seen-by row on a cook would quietly turn a
 *    small domestic fact into something with an audience, and the entry is authored often
 *    enough — every dinner — that "who noticed" is one short step from "who noticed most".
 *    That is the field-that-could-grow-into-a-metric the check-in contract forbids
 *    (`packages/shared/src/modules/checkin/index.ts`), so: no `seenBy`, no stamping, no row.
 *  - **No tallies.** Same rule as the games archive (`apps/web/src/modules/games/
 *    archive.ts`) and the cook log's own schema — each finished game and each cook is its
 *    own memory, never aggregated into a score. The feed counts nothing.
 *  - **Main feed only.** The check-in archive (`CheckinArchivePage.tsx`, `groupByMonth`)
 *    stays posts-only; games keep their own archive, and a cook's home is its recipe.
 *  - **No reordering or promotion.** A fact's place in the feed is exactly where its
 *    instant sorts it — same "nothing is ever promoted, resurfaced, or reordered" rule as
 *    `feed.ts`'s day-grouping.
 *
 * All of it is pure so the merge and grouping rules are testable without a browser.
 */

export type FeedItem =
  | { kind: 'checkin'; instant: string; checkin: Checkin }
  | { kind: 'game'; instant: string; game: GameMeta }
  | { kind: 'cook'; instant: string; cook: CookLogEntry };

export interface FeedItemGroup {
  /** `YYYY-MM-DD` — stable React key, matches `feed.ts`'s day keys. */
  key: string;
  label: string;
  items: FeedItem[];
}

/** The id of whatever the item wraps — a `switch` rather than a ternary chain so adding a
 * fourth kind is a compile error here instead of a silently wrong sort key. */
export function itemId(item: FeedItem): string {
  switch (item.kind) {
    case 'checkin':
      return item.checkin.id;
    case 'game':
      return item.game.id;
    case 'cook':
      return item.cook.id;
  }
}

/** Only `status: 'done'` games ever reach the feed — an open or active match isn't a fact
 * yet, it's a thing still happening (and the board itself is where "your turn" lives). */
export function finishedGames(games: readonly GameMeta[]): GameMeta[] {
  return games.filter((game) => game.status === 'done');
}

/** A finished game's feed instant: `finishedAt` once stamped (WS-17b), falling back to
 * `updatedAt` for games finished before that field existed — same fallback the games
 * archive uses, so a game sorts into the same day/month everywhere it's shown. */
export function gameInstant(game: GameMeta): string {
  return game.finishedAt ?? game.updatedAt;
}

/** Combines check-ins, finished games and cooks into one flat, unsorted list of feed items —
 * the read-side merge, nothing more. Every cook reaches the feed: unlike a game there is no
 * unfinished state to filter out, because a cook only exists once it has already happened. */
export function buildFeedItems(
  checkins: readonly Checkin[],
  games: readonly GameMeta[],
  cooks: readonly CookLogEntry[],
): FeedItem[] {
  const checkinItems: FeedItem[] = checkins.map((checkin) => ({
    kind: 'checkin',
    instant: checkin.createdAt,
    checkin,
  }));
  const gameItems: FeedItem[] = finishedGames(games).map((game) => ({
    kind: 'game',
    instant: gameInstant(game),
    game,
  }));
  const cookItems: FeedItem[] = cooks.map((cook) => ({
    kind: 'cook',
    instant: cook.cookedAt,
    cook,
  }));
  return [...checkinItems, ...gameItems, ...cookItems];
}

/** Newest first across every kind, ties broken by id so nothing shuffles between renders. */
export function sortFeedItemsNewestFirst(items: FeedItem[]): FeedItem[] {
  return [...items].sort((a, b) => {
    const diff = Date.parse(b.instant) - Date.parse(a.instant);
    return diff !== 0 ? diff : itemId(b).localeCompare(itemId(a));
  });
}

/** The feed, generalized: day headers, newest day first, check-ins, finished games and
 * cooks interleaved strictly by instant within a day — same rule as `feed.ts`'s
 * `groupByDay`, extended to a mixed item list. */
export function groupFeedItemsByDay(
  items: FeedItem[],
  now: Date = new Date(),
  timeZone?: string,
): FeedItemGroup[] {
  const buckets = new Map<string, FeedItem[]>();
  for (const item of sortFeedItemsNewestFirst(items)) {
    const key = dayKey(item.instant, timeZone);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, groupItems]) => ({ key, label: dayLabel(key, now, timeZone), items: groupItems }));
}
